import { useEffect, useRef, useState } from 'react'
import { api } from '../lib/api.ts'
import { branchesIn, type SyncBranch } from '../lib/syncRefusal.ts'
import { Button } from './Button.tsx'
import { useToast } from './Toast.tsx'
import { FOCUS_RING_ROW } from './focusRing.ts'

/**
 * Pulling in an orçamento discovery has not brought.
 *
 * Discovery reads forward from a watermark, which is what makes a pass a small
 * indexed read instead of a re-sweep -- and is also why an orçamento the
 * watermark has already passed can never arrive on its own. Somebody searching
 * the queue for a number and finding nothing had no way to say "fetch this
 * one", and the answer was to ask an engineer.
 *
 * Offered only where a search for a number found nothing. Every other empty
 * result is a filter, and answering those with a button to create something
 * would be answering a question nobody asked.
 *
 * ## No filial up front (#465)
 *
 * The person knows the number they just keyed, not always its filial, and a
 * selector in front of the button made the common case -- one filial has it --
 * a choice to get right. The server asks the ERP which connected filiais have
 * the number; only when more than one does is the person asked, in a modal.
 * Every answer the server gives is said in a toast, in its own words: "not in
 * the ERP", "ERP unreachable" and "nothing configured" ask different things of
 * whoever pressed the button.
 */
export function SyncQuotation({
  quotationNumber,
  branchCode,
  onOpened,
}: {
  quotationNumber: number
  /**
   * The filial a scanned barcode named. Given, the fetch starts on its own:
   * a scan is never half-typed and says exactly which requisição it is, so
   * a button to confirm it would only be a second scan.
   */
  branchCode?: number | undefined
  onOpened: (found: { id: string; branch: number; quotation: number }) => void
}) {
  const toast = useToast()
  // Busy from the first paint on a scan, so the button never flashes before the fetch.
  const [busy, setBusy] = useState(branchCode !== undefined)
  const [choices, setChoices] = useState<readonly Choice[] | undefined>()

  const sync = async (code?: number) => {
    setBusy(true)
    try {
      const found = await api.syncQuotation(quotationNumber, code)
      setChoices(undefined)
      onOpened({
        id: found.verificationId,
        branch: found.branchCode ?? code ?? 0,
        quotation: quotationNumber,
      })
    } catch (cause) {
      const several = branchesIn(cause)
      if (several.length > 1) {
        setChoices(several)
        return
      }
      toast.show(`Não foi possível buscar o orçamento ${quotationNumber}`, {
        tone: 'error',
        detail: cause instanceof Error && cause.message !== '' ? cause.message : undefined,
      })
    } finally {
      setBusy(false)
    }
  }

  // Once per mount, so a re-render or StrictMode's double effect cannot fetch twice.
  const fetchedOnScan = useRef(false)
  useEffect(() => {
    if (branchCode === undefined || fetchedOnScan.current) return
    fetchedOnScan.current = true
    void sync(branchCode)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchCode])

  return (
    <div className="flex flex-col items-center gap-2 py-2">
      <span className="text-tP text-text-weak">
        Nenhuma conferência para o orçamento {quotationNumber}.
      </span>
      <span className="text-tSmall text-text-weak">
        Se ele existe no ERP, podemos buscá-lo agora e conferir.
      </span>
      <Button
        variant="secondary"
        tone="brand"
        size="small"
        disabled={busy}
        onClick={() => void sync(branchCode)}
      >
        {busy ? 'Buscando…' : 'Buscar no ERP'}
      </Button>
      {choices !== undefined && (
        <ChooseBranch
          quotationNumber={quotationNumber}
          choices={choices}
          busy={busy}
          onChoose={(code) => void sync(code)}
          onClose={() => setChoices(undefined)}
        />
      )}
    </div>
  )
}

type Choice = SyncBranch

/** Which filial, when the number exists at more than one. */
function ChooseBranch({
  quotationNumber,
  choices,
  busy,
  onChoose,
  onClose,
}: {
  quotationNumber: number
  choices: readonly Choice[]
  busy: boolean
  onChoose: (code: number) => void
  onClose: () => void
}) {
  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-[rgba(0,9,51,0.35)] p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`Filial do orçamento ${quotationNumber}`}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) onClose()
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && !busy) onClose()
      }}
    >
      <div className="rounded-base bg-background-raised border-stroke-weak flex w-full max-w-sm flex-col gap-3 border p-4">
        <div className="flex flex-col gap-1">
          <h2 className="text-tPSemi text-text-strong">Em qual filial?</h2>
          <span className="text-tSmall text-text-weak">
            O orçamento {quotationNumber} existe em mais de uma filial.
          </span>
        </div>
        <ul className="flex flex-col">
          {choices.map((choice, index) => (
            <li key={choice.code}>
              <button
                type="button"
                disabled={busy}
                autoFocus={index === 0}
                className={`text-tP text-text-strong hover:bg-fill-weak flex w-full items-baseline justify-between rounded-[6px] px-2 py-2 text-left disabled:opacity-50 ${FOCUS_RING_ROW}`}
                onClick={() => onChoose(choice.code)}
              >
                <span>{choice.name}</span>
                <span className="text-tSmall text-text-weak">Filial {choice.code}</span>
              </button>
            </li>
          ))}
        </ul>
        <div className="flex justify-end">
          <Button variant="tertiary" size="small" disabled={busy} onClick={onClose}>
            Cancelar
          </Button>
        </div>
      </div>
    </div>
  )
}
